import React from "react";
import { ShoppingBasket, HeartHandshake, Sprout } from "lucide-react";
import { Reveal, WfButton } from "@/components/site/primitives";
import { Link } from "react-router-dom";

const callouts = [
  { id: "where-to-buy", to: "/where-to-buy", icon: ShoppingBasket, title: "Where to buy crops", text: "Fresh produce near you." },
  { id: "support", to: "/support", icon: HeartHandshake, title: "I want to support", text: "Give and make a difference." },
  { id: "who-we-are", to: "/about", icon: Sprout, title: "Who we are", text: "Four decades of community." },
];

export const FinalCTAV4 = () => {
  return (
    <section id="get-involved" aria-labelledby="final-cta-v4-heading" className="bg-white py-16 sm:py-20 lg:py-28">
      <div className="wf-container">
        <div className="relative overflow-hidden rounded-sm bg-[var(--wf-sage)] px-6 py-16 text-center sm:px-10 sm:py-20">
          {/* Bright field photo filling the whole box */}
          <img
            src="/images/wf/farm-3503.jpg"
            alt="Close-up of a farmer's hands bundling freshly harvested greens in a sunlit field."
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* Warm light wash keeps the box bright and the dark text readable */}
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-[rgba(255,253,248,0.86)] via-[rgba(255,253,248,0.78)] to-[rgba(233,223,201,0.82)]" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[var(--wf-moss)]/40 blur-2xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-[var(--wf-wheat)]/60 blur-2xl" />
          <Reveal className="relative mx-auto max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Join the community</span>
            <h2 id="final-cta-v4-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.08] text-[var(--wf-soil)] sm:text-5xl">
              Every farmer’s success starts with a community that shows up.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[var(--wf-soil)]/80">
              Shop the harvest, give what you can, or get to know the people growing it.
            </p>
            <div className="mt-8">
              <WfButton as={Link} to="/support" withArrow className="px-7 text-base" data-testid="final-cta-get-involved-button">Get Involved</WfButton>
            </div>
          </Reveal>

          <div className="relative mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            {callouts.map((c, idx) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.id} delay={0.07 * idx}>
                  <Link to={c.to} data-testid={`final-cta-callout-${c.id}`} className="group flex w-full flex-col items-start gap-2 rounded-sm border border-white/70 bg-white/70 p-5 text-left backdrop-blur-sm transition-colors duration-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-farm-green)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--wf-sage)]">
                    <span className="grid h-10 w-10 place-items-center rounded-sm bg-[var(--wf-farm-green)] text-white"><Icon className="h-5 w-5" aria-hidden="true" /></span>
                    <span className="font-serif text-lg font-semibold text-[var(--wf-soil)]">{c.title}</span>
                    <span className="text-sm text-[var(--wf-soil)]/75">{c.text}</span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
